import { supabase } from '@/integrations/supabase/client';
import { normalizePlatform, PLATFORMS, PLATFORM_REGISTRY } from '@/lib/platformConstants';

// Types for POS sales item mapping
export interface POSSalesItem {
  id: string; // Unique identifier (could be item_name or a hash)
  name: string;
  platform: string;
  totalQuantity?: number;
  totalRevenue?: number;
  occurrences?: number;
}

export interface MappedItem {
  id: string;
  name: string;
  type: 'menu_item' | 'ingredient' | 'prep_item';
  category?: string;
  unit?: string;
}

export interface POSSalesItemMatch {
  posItem: POSSalesItem;
  mappedItem: MappedItem | null;
  confidence: number;
  matchReason: string;
}

export interface POSSalesItemGroup {
  id: string; // Unique group identifier
  patternName: string; // Display name for the group
  items: POSSalesItem[];
  isTrained: boolean;
  platform: string;
  suggestion?: POSSalesItemMatch;
}

export type MappingType = 'menu_item' | 'ingredient' | 'prep_item' | 'menu_category' | 'non_menu' | 'ignored';

export interface POSSalesItemMapping {
  company_id: string;
  platform: string;
  pos_item_identifier: string;
  pos_item_name: string;
  mapped_type: MappingType;
  mapped_item_id: string | null;
  auto_matched: boolean;
  confidence?: number;
}

// ============================================================================
// String Similarity Functions
// ============================================================================

/**
 * Calculates Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  const len1 = str1.length;
  const len2 = str2.length;
  
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1
        );
      }
    }
  }
  
  return matrix[len1][len2];
}

/**
 * Calculates similarity score between two strings (0-1)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1;
  const distance = levenshteinDistance(str1.toLowerCase().trim(), str2.toLowerCase().trim());
  return 1 - (distance / maxLen);
}

/**
 * Normalizes a name for comparison
 */
function normalizeName(name: string): string {
  return name
    .toLowerCase()
    // Remove size variants
    .replace(/\b(rs|sm|md|lg|xl|xxl|regular|small|medium|large)\b/gi, '')
    // Remove package indicators
    .replace(/\b(cs|bx|pk|case|box|pack|ct|count|ea|each|per)\b/gi, '')
    // Remove size indicators like "12oz", "5lb"
    .replace(/\b\d+\s*(oz|lb|lbs|g|kg|ml|l|gal|pt|qt)\b/gi, '')
    // Keep alphanumeric and spaces
    .replace(/[^\w\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extracts key words from a name (removes common words)
 */
function extractKeyWords(name: string): string[] {
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'combo', 'meal', 'set', 'special', 'deluxe', 'premium', 'extra', 'add', 'side',
  ]);
  
  const normalized = normalizeName(name);
  return normalized
    .split(' ')
    .filter(word => word.length > 2 && !stopWords.has(word));
}

/**
 * Calculates similarity between two product names using combined methods
 */
export function calculateProductSimilarity(name1: string, name2: string): number {
  const n1 = normalizeName(name1);
  const n2 = normalizeName(name2);
  
  // Exact match after normalization
  if (n1 === n2) return 1.0;
  if (!n1 || !n2) return 0;
  
  // Check for significant substring match (one contains the other)
  if (n1.length >= 4 && n2.length >= 4) {
    if (n1.includes(n2) || n2.includes(n1)) return 0.9;
  }
  
  // Direct string similarity
  const stringSimilarity = calculateSimilarity(n1, n2);
  
  // Word-based Jaccard similarity
  const words1 = new Set(n1.split(' ').filter(w => w.length > 2));
  const words2 = new Set(n2.split(' ').filter(w => w.length > 2));
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  const jaccardSimilarity = union.size > 0 ? intersection.size / union.size : 0;
  
  // Combined score (weighted average)
  return (stringSimilarity * 0.6) + (jaccardSimilarity * 0.4);
}

// ============================================================================
// Grouping Functions
// ============================================================================

/**
 * Groups similar POS items using similarity threshold
 * Aggressively groups similar items across ALL platforms, not just same-named items
 * @param items - Array of POS sales items to group
 * @param similarityThreshold - Minimum similarity score to group items (default 0.70, lowered for better cross-platform grouping)
 * @returns Array of grouped items
 */
export function groupSimilarPOSItems(
  items: POSSalesItem[],
  similarityThreshold: number = 0.65  // Lowered threshold for better cross-platform matching
): POSSalesItemGroup[] {
  if (items.length === 0) return [];
  
  console.warn(`🔄 groupSimilarPOSItems: Starting grouping of ${items.length} items with threshold ${similarityThreshold}`);
  const itemsByPlatform = items.reduce((acc, item) => {
    acc[item.platform] = (acc[item.platform] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  console.warn('📊 Items by platform:', itemsByPlatform);
  
  const groups: POSSalesItemGroup[] = [];
  const processed = new Set<string>();
  
  // Step 1: Create initial clusters by comparing ALL items across ALL platforms
  // This allows "water", "Water", "H2O", etc. to be grouped together
  for (let i = 0; i < items.length; i++) {
    const currentItem = items[i];
    
    // Skip if already processed
    if (processed.has(currentItem.id)) continue;
    
    const cluster: POSSalesItem[] = [currentItem];
    processed.add(currentItem.id);
    
    // Compare with all remaining items (across all platforms)
    for (let j = i + 1; j < items.length; j++) {
      const otherItem = items[j];
      
      // Skip if already processed
      if (processed.has(otherItem.id)) continue;
      
      // Calculate similarity between items
      const similarity = calculateProductSimilarity(
        currentItem.name,
        otherItem.name
      );
      
      // If similar enough, add to cluster
      if (similarity >= similarityThreshold) {
        cluster.push(otherItem);
        processed.add(otherItem.id);
        console.warn(`✅ Grouped: "${currentItem.name}" (${currentItem.platform}) with "${otherItem.name}" (${otherItem.platform}) - similarity: ${(similarity * 100).toFixed(1)}%`);
      }
    }
    
    // Use the longest/most descriptive name as the pattern
    const patternName = cluster.reduce((longest, item) => 
      item.name.length > longest.length ? item.name : longest,
      cluster[0].name
    );
    
    // Determine platform(s) for the group - show all platforms
    const platformsInGroup = new Set(cluster.map(item => item.platform));
    const groupPlatform = platformsInGroup.size === 1 
      ? Array.from(platformsInGroup)[0]
      : Array.from(platformsInGroup).sort().join(',');
    
    // Log cross-platform groups
    if (platformsInGroup.size > 1) {
      console.warn(`🌐 Cross-platform group: "${patternName}" with ${cluster.length} items from: ${Array.from(platformsInGroup).join(', ')}`);
    }
    
    // Generate unique group ID
    const groupId = `${groupPlatform}-${currentItem.id}-${Date.now()}`;
    
    groups.push({
      id: groupId,
      patternName,
      items: cluster,
      isTrained: false,
      platform: groupPlatform,
    });
  }
  
  // Log grouping summary
  const crossPlatformGroups = groups.filter(g => g.platform.includes(','));
  console.warn(`✅ groupSimilarPOSItems: Created ${groups.length} groups total, ${crossPlatformGroups.length} cross-platform groups`);
  const groupsByPlatform = groups.reduce((acc, group) => {
    const key = group.platform.includes(',') ? 'cross-platform' : group.platform;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  console.warn('📊 Groups by platform:', groupsByPlatform);
  
  return groups;
}

/**
 * Ungroups selected items from a group, creating new individual groups
 * @param groups - Current array of groups
 * @param groupId - ID of the group to ungroup from
 * @param selectedItemIds - IDs of items to remove and create as new groups
 * @returns Updated array of groups with ungrouped items as new groups
 */
export function ungroupItems(
  groups: POSSalesItemGroup[],
  groupId: string,
  selectedItemIds: string[]
): POSSalesItemGroup[] {
  const result: POSSalesItemGroup[] = [];
  
  for (const group of groups) {
    if (group.id !== groupId) {
      result.push(group);
      continue;
    }
    
    // Split the group
    const remainingItems = group.items.filter(item => !selectedItemIds.includes(item.id));
    const ungroupedItems = group.items.filter(item => selectedItemIds.includes(item.id));
    
    // Keep original group with remaining items (if any)
    if (remainingItems.length > 0) {
      const newPatternName = remainingItems.reduce((longest, item) => 
        item.name.length > longest.length ? item.name : longest,
        remainingItems[0].name
      );
      
      result.push({
        ...group,
        patternName: newPatternName,
        items: remainingItems,
      });
    }
    
    // Create new individual groups for each ungrouped item
    // Use the item's own platform instead of the group's platform (which might be comma-separated)
    for (let i = 0; i < ungroupedItems.length; i++) {
      const item = ungroupedItems[i];
      // Use item's platform, not group's platform (group platform might be comma-separated for cross-platform groups)
      const itemPlatform = item.platform || (group.platform.includes(',') 
        ? group.platform.split(',')[0].trim() 
        : group.platform);
      
      // Generate unique ID with index to avoid collisions
      const uniqueId = `${itemPlatform}-${item.id}-ungrouped-${Date.now()}-${i}`;
      
      result.push({
        id: uniqueId,
        patternName: item.name,
        items: [item],
        isTrained: false,
        platform: itemPlatform,
      });
    }
  }
  
  return result;
}

// ============================================================================
// Matching Functions
// ============================================================================

/**
 * Finds the best matching item (menu item, ingredient, or prep item) for a POS item
 */
export async function findBestMatch(
  posItem: POSSalesItem,
  companyId: string
): Promise<POSSalesItemMatch> {
  // Load all potential matches
  const [menuItemsResult, ingredientsResult, prepItemsResult] = await Promise.all([
    supabase
      .from('menu_items')
      .select('id, name, category_id, menu_categories(name)')
      .eq('company_id', companyId),
    supabase
      .from('ingredients')
      .select('id, name, category, standard_unit')
      .eq('company_id', companyId),
    supabase
      .from('prep_items')
      .select('id, name, yield_unit')
      .eq('company_id', companyId),
  ]);
  
  const menuItems = menuItemsResult.data || [];
  const ingredients = ingredientsResult.data || [];
  const prepItems = prepItemsResult.data || [];
  
  if (menuItems.length === 0 && ingredients.length === 0 && prepItems.length === 0) {
    return {
      posItem,
      mappedItem: null,
      confidence: 0,
      matchReason: 'No items found to match against',
    };
  }
  
  let bestMatch: MappedItem | null = null;
  let bestConfidence = 0;
  let bestReason = '';
  
  const posName = normalizeName(posItem.name);
  const posKeywords = new Set(extractKeyWords(posItem.name));
  
  // Check ingredients FIRST (prioritize ingredients)
  for (const item of ingredients) {
    const itemName = normalizeName(item.name);
    const nameSimilarity = calculateSimilarity(posName, itemName);
    
    const itemKeywords = new Set(extractKeyWords(item.name));
    const intersection = new Set([...posKeywords].filter(x => itemKeywords.has(x)));
    const union = new Set([...posKeywords, ...itemKeywords]);
    const keywordSimilarity = union.size > 0 ? intersection.size / union.size : 0;
    
    let confidence = (nameSimilarity * 0.7) + (keywordSimilarity * 0.3);
    
    // Boost for exact substring match
    if (posName.includes(itemName) || itemName.includes(posName)) {
      confidence = Math.min(confidence * 1.2, 0.95);
    }
    
    if (confidence > bestConfidence) {
      bestConfidence = confidence;
      bestMatch = {
        id: item.id,
        name: item.name,
        type: 'ingredient',
        category: item.category,
        unit: item.standard_unit,
      };
      bestReason = `Ingredient match: ${(confidence * 100).toFixed(0)}%`;
    }
  }
  
  // Check menu items (after ingredients)
  for (const item of menuItems) {
    const itemName = normalizeName(item.name);
    const nameSimilarity = calculateSimilarity(posName, itemName);
    
    const itemKeywords = new Set(extractKeyWords(item.name));
    const intersection = new Set([...posKeywords].filter(x => itemKeywords.has(x)));
    const union = new Set([...posKeywords, ...itemKeywords]);
    const keywordSimilarity = union.size > 0 ? intersection.size / union.size : 0;
    
    let confidence = (nameSimilarity * 0.7) + (keywordSimilarity * 0.3);
    
    // Boost for exact substring match
    if (posName.includes(itemName) || itemName.includes(posName)) {
      confidence = Math.min(confidence * 1.2, 0.95);
    }
    
    // Slight penalty for menu item matches (prefer ingredients)
    confidence *= 0.95;
    
    if (confidence > bestConfidence) {
      bestConfidence = confidence;
      bestMatch = {
        id: item.id,
        name: item.name,
        type: 'menu_item',
        category: (item as any).menu_categories?.name,
      };
      bestReason = `Menu item match: ${(confidence * 100).toFixed(0)}%`;
    }
  }
  
  // Check prep items
  for (const item of prepItems) {
    const itemName = normalizeName(item.name);
    const nameSimilarity = calculateSimilarity(posName, itemName);
    
    const itemKeywords = new Set(extractKeyWords(item.name));
    const intersection = new Set([...posKeywords].filter(x => itemKeywords.has(x)));
    const union = new Set([...posKeywords, ...itemKeywords]);
    const keywordSimilarity = union.size > 0 ? intersection.size / union.size : 0;
    
    let confidence = (nameSimilarity * 0.7) + (keywordSimilarity * 0.3);
    
    // Slight penalty for prep item matches (prefer menu items)
    confidence *= 0.93;
    
    if (confidence > bestConfidence) {
      bestConfidence = confidence;
      bestMatch = {
        id: item.id,
        name: item.name,
        type: 'prep_item',
        category: undefined,
        unit: item.yield_unit,
      };
      bestReason = `Prep item match: ${(confidence * 100).toFixed(0)}%`;
    }
  }
  
  // Only return match if confidence is above threshold
  if (bestMatch && bestConfidence >= 0.6) {
    return {
      posItem,
      mappedItem: bestMatch,
      confidence: bestConfidence,
      matchReason: bestReason,
    };
  }
  
  return {
    posItem,
    mappedItem: null,
    confidence: bestConfidence,
    matchReason: bestConfidence > 0 
      ? `Best match: ${(bestConfidence * 100).toFixed(0)}% (below threshold)` 
      : 'No match found',
  };
}

/**
 * Auto-matches POS items to menu items/ingredients/prep items in batch
 */
export async function autoMatchPOSItems(
  items: POSSalesItem[],
  companyId: string
): Promise<POSSalesItemMatch[]> {
  const matches: POSSalesItemMatch[] = [];
  
  // Process in batches
  const batchSize = 20;
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchMatches = await Promise.all(
      batch.map(item => findBestMatch(item, companyId))
    );
    matches.push(...batchMatches);
  }
  
  return matches;
}

// ============================================================================
// Database Operations
// ============================================================================

/**
 * Saves a POS sales item mapping
 */
export async function savePOSSalesItemMapping(
  companyId: string,
  platform: string,
  posItemIdentifier: string,
  posItemName: string,
  mappedType: MappingType,
  mappedItemId: string | null,
  autoMatched: boolean = false,
  confidence?: number
): Promise<void> {
  // We'll use the existing pos_menu_item_mappings table but extend our usage
  // The mapped_type and mapped_item_id will be stored in the existing columns
  // menu_item_id for backward compatibility, and we add a new approach
  
  const mapping: any = {
    company_id: companyId,
    platform,
    pos_item_identifier: posItemIdentifier,
    pos_item_name: posItemName,
    auto_matched: autoMatched,
    is_non_menu_item: mappedType === 'non_menu',
    is_ignored: mappedType === 'ignored',
  };
  
  // Store mapping based on type
  if (mappedType === 'menu_item') {
    mapping.menu_item_id = mappedItemId;
    mapping.mapped_type = 'menu_item';
    mapping.mapped_item_id = mappedItemId;
  } else if (mappedType === 'ingredient') {
    mapping.menu_item_id = null;
    mapping.mapped_type = 'ingredient';
    mapping.mapped_item_id = mappedItemId;
  } else if (mappedType === 'prep_item') {
    mapping.menu_item_id = null;
    mapping.mapped_type = 'prep_item';
    mapping.mapped_item_id = mappedItemId;
  } else if (mappedType === 'menu_category') {
    mapping.menu_item_id = null;
    mapping.mapped_type = 'menu_category';
    mapping.mapped_item_id = mappedItemId;
  } else {
    mapping.menu_item_id = null;
    mapping.mapped_type = mappedType;
    mapping.mapped_item_id = null;
  }
  
  if (confidence !== undefined) {
    mapping.confidence = confidence;
  }
  
  const { error } = await supabase
    .from('pos_menu_item_mappings')
    .upsert(mapping, {
      onConflict: 'company_id,platform,pos_item_identifier',
    });
  
  if (error) throw error;
}

/**
 * Gets all mappings for a company/platform
 */
export async function getPOSSalesItemMappings(
  companyId: string,
  platform?: string
): Promise<POSSalesItemMapping[]> {
  let query = supabase
    .from('pos_menu_item_mappings')
    .select('*')
    .eq('company_id', companyId);
  
  if (platform) {
    const canonical = normalizePlatform(platform);
    const entry = PLATFORM_REGISTRY.find(p => p.id === canonical);
    const allVariations = entry
      ? [canonical, ...entry.aliases.map(a => a.toLowerCase())]
      : [canonical, platform.toLowerCase().trim()];
    const uniqueVariations = [...new Set(allVariations)];
    
    query = query.in('platform', uniqueVariations);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  // Transform to our interface
  return (data || []).map((row: any) => ({
    company_id: row.company_id,
    platform: row.platform,
    pos_item_identifier: row.pos_item_identifier,
    pos_item_name: row.pos_item_name,
    mapped_type: row.mapped_type || (row.is_ignored ? 'ignored' : row.is_non_menu_item ? 'non_menu' : row.menu_item_id ? 'menu_item' : 'non_menu'),
    mapped_item_id: row.mapped_item_id || row.menu_item_id,
    auto_matched: row.auto_matched,
    confidence: row.confidence,
  }));
}

/**
 * Loads unique POS items from menu_performance for a platform
 */
export async function loadUniquePOSItems(
  companyId: string,
  platform?: string
): Promise<POSSalesItem[]> {
  let query = supabase
    .from('menu_performance')
    .select('item_name, source, quantity_sold, revenue')
    .eq('company_id', companyId);
  
  // Filter by platform if provided
  if (platform) {
    // Source contains platform name (e.g., "Toast - Manual", "DoorDash - Auto Sync")
    query = query.ilike('source', `%${platform}%`);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  // Aggregate unique items
  const itemMap = new Map<string, POSSalesItem>();
  
  for (const row of data || []) {
    const key = `${row.item_name}-${row.source?.split(' - ')[0] || 'unknown'}`;
    const existingItem = itemMap.get(key);
    
    const sourcePlatform = normalizePlatform(row.source?.split(' - ')[0]);
    
    if (existingItem) {
      existingItem.totalQuantity = (existingItem.totalQuantity || 0) + (row.quantity_sold || 0);
      existingItem.totalRevenue = (existingItem.totalRevenue || 0) + (row.revenue || 0);
      existingItem.occurrences = (existingItem.occurrences || 0) + 1;
    } else {
      itemMap.set(key, {
        id: key,
        name: row.item_name,
        platform: sourcePlatform,
        totalQuantity: row.quantity_sold || 0,
        totalRevenue: row.revenue || 0,
        occurrences: 1,
      });
    }
  }
  
  return Array.from(itemMap.values());
}

/**
 * Loads unique POS items from pos_report_uploads for a platform
 */
export async function loadPOSItemsFromReports(
  companyId: string,
  platform: string
): Promise<POSSalesItem[]> {
  const normalizedPlatform = normalizePlatform(platform);
  const entry = PLATFORM_REGISTRY.find(p => p.id === normalizedPlatform);
  const allVariations = entry
    ? [normalizedPlatform, ...entry.aliases.map(a => a.toLowerCase())]
    : [normalizedPlatform, platform.toLowerCase().trim()];
  const uniqueVariations = [...new Set(allVariations)];
  
  const { data: reports, error } = await supabase
    .from('pos_report_uploads')
    .select('processed_data, platform, report_type, status')
    .eq('company_id', companyId)
    .in('platform', uniqueVariations)
    .eq('report_type', 'item_quantities')
    .eq('status', 'completed')
    .order('created_at', { ascending: false })
    .limit(1);
  
  if (error) {
    console.error(`loadPOSItemsFromReports: Error querying for platform ${normalizedPlatform}:`, error);
    throw error;
  }
  
  if (!reports || reports.length === 0) {
    console.log(`loadPOSItemsFromReports: No reports found for platform ${normalizedPlatform}`);
    
    // Debug: Check if there are any reports for this platform with different status
    const { data: allReports } = await supabase
      .from('pos_report_uploads')
      .select('platform, report_type, status')
      .eq('company_id', companyId)
      .eq('platform', normalizedPlatform)
      .eq('report_type', 'item_quantities');
    
    if (allReports && allReports.length > 0) {
      console.log(`loadPOSItemsFromReports: Found ${allReports.length} reports but none are completed:`, allReports);
    } else {
      console.log(`loadPOSItemsFromReports: No reports at all for platform ${normalizedPlatform}`);
    }
    
    return [];
  }
  
  if (!reports[0]?.processed_data) {
    console.log(`loadPOSItemsFromReports: Report found but no processed_data for platform ${normalizedPlatform}`);
    return [];
  }
  
  const processedData = reports[0].processed_data as any;
  console.log(`loadPOSItemsFromReports: Found processed_data for ${platform}:`, 
    typeof processedData, 
    Array.isArray(processedData) ? `array[${processedData.length}]` : Object.keys(processedData || {})
  );
  
  // Handle new format: { raw_data: [...], item_identifiers: [...] }
  let identifiers = processedData?.item_identifiers;
  const rawData = processedData?.raw_data;
  
  // Aggregate quantities from raw_data for each item
  const quantityMap = new Map<string, { quantity: number; revenue: number }>();
  if (rawData && Array.isArray(rawData)) {
    rawData.forEach((item: any) => {
      if (item.item_name) {
        const key = item.item_name.toLowerCase().trim();
        const existing = quantityMap.get(key) || { quantity: 0, revenue: 0 };
        quantityMap.set(key, {
          quantity: existing.quantity + (item.quantity_sold || 0),
          revenue: existing.revenue + (item.revenue || 0),
        });
      }
    });
    console.warn(`loadPOSItemsFromReports: Built quantityMap with ${quantityMap.size} items from raw_data`);
  }
  
  // Handle old format: array with item_identifiers property (might not survive JSON serialization)
  if (!identifiers && Array.isArray(processedData)) {
    // If it's an array of ParsedQuantityData, extract unique items from it
    console.warn(`loadPOSItemsFromReports: Falling back to raw_data extraction for ${platform}`);
    const uniqueItems = new Map<string, any>();
    processedData.forEach((item: any) => {
      if (item.item_name) {
        const key = item.item_name.toLowerCase().trim();
        const existing = uniqueItems.get(key);
        if (!existing) {
          uniqueItems.set(key, {
            identifier: item.item_name,
            name: item.item_name,
            platform: item.platform || platform,
            quantity: item.quantity_sold || 0,
            revenue: item.revenue || 0,
          });
        } else {
          // Aggregate quantities
          existing.quantity += (item.quantity_sold || 0);
          existing.revenue += (item.revenue || 0);
        }
      }
    });
    identifiers = Array.from(uniqueItems.values());
  }
  
  // Handle case where raw_data exists but item_identifiers doesn't
  if (!identifiers && rawData && Array.isArray(rawData)) {
    console.warn(`loadPOSItemsFromReports: Extracting from raw_data for ${platform}`);
    const uniqueItems = new Map<string, any>();
    rawData.forEach((item: any) => {
      if (item.item_name) {
        const key = item.item_name.toLowerCase().trim();
        const existing = uniqueItems.get(key);
        if (!existing) {
          uniqueItems.set(key, {
            identifier: item.item_name,
            name: item.item_name,
            platform: item.platform || platform,
            quantity: item.quantity_sold || 0,
            revenue: item.revenue || 0,
          });
        } else {
          // Aggregate quantities
          existing.quantity += (item.quantity_sold || 0);
          existing.revenue += (item.revenue || 0);
        }
      }
    });
    identifiers = Array.from(uniqueItems.values());
  }
  
  if (!identifiers || identifiers.length === 0) {
    console.warn(`loadPOSItemsFromReports: No identifiers found for ${platform}`);
    return [];
  }
  
  console.warn(`loadPOSItemsFromReports: Found ${identifiers.length} identifiers for ${platform}`);
  
  return identifiers.map((item: any) => {
    const itemPlatform = normalizePlatform(item.platform || platform);
    
    // Get aggregated quantities - prioritize from quantityMap (raw_data), then from item itself, then 0
    const itemKey = item.name?.toLowerCase().trim() || item.identifier?.toLowerCase().trim();
    const aggregated = quantityMap.get(itemKey || '');
    
    // Use quantity from aggregated raw_data first, then from item_identifiers, then 0
    const totalQuantity = aggregated?.quantity ?? item.quantity ?? 0;
    const totalRevenue = aggregated?.revenue ?? item.revenue ?? 0;
    
    // Log if quantity is missing for debugging
    if (totalQuantity === 0 && itemKey) {
      console.warn(`loadPOSItemsFromReports: No quantity found for item "${item.name}" (key: "${itemKey}"). quantityMap has:`, Array.from(quantityMap.keys()).slice(0, 5));
    }
    
    // Create unique ID that includes platform to avoid collisions across platforms
    const uniqueId = `${itemPlatform}:${item.identifier || item.name}`;
    
    return {
      id: uniqueId,
      name: item.name,
      platform: itemPlatform,
      totalQuantity,
      totalRevenue,
      occurrences: 1,
    };
  });
}

/**
 * Applies saved mappings to new menu_performance data
 * This should be called after inserting new data to apply known mappings
 */
export async function applyMappingsToMenuPerformance(
  companyId: string,
  platform?: string
): Promise<number> {
  // Get all mappings
  const mappings = await getPOSSalesItemMappings(companyId, platform);
  
  if (mappings.length === 0) return 0;
  
  // For now, we don't update menu_performance directly
  // The mapping is used at query time to join data
  // If schema supports mapped_item_id column, we could update here
  
  return mappings.length;
}

/**
 * Gets the mapping for a specific POS item
 */
export async function getMappingForPOSItem(
  companyId: string,
  platform: string,
  posItemIdentifier: string
): Promise<POSSalesItemMapping | null> {
  const { data, error } = await supabase
    .from('pos_menu_item_mappings')
    .select('*')
    .eq('company_id', companyId)
    .eq('platform', platform)
    .eq('pos_item_identifier', posItemIdentifier)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error;
  
  if (!data) return null;
  
  // Type assertion to handle optional fields that may not be in the generated types
  const dataAny = data as any;
  return {
    company_id: data.company_id,
    platform: data.platform,
    pos_item_identifier: data.pos_item_identifier,
    pos_item_name: data.pos_item_name,
    mapped_type: dataAny.mapped_type || (data.is_ignored ? 'ignored' : data.is_non_menu_item ? 'non_menu' : data.menu_item_id ? 'menu_item' : 'non_menu'),
    mapped_item_id: dataAny.mapped_item_id || data.menu_item_id,
    auto_matched: data.auto_matched,
    confidence: dataAny.confidence,
  };
}
